'use client';

import { Children, type ReactNode, type RefCallback } from 'react';
import Link from 'next/link';
import { EntityPreviewCard, EntityPreviewTrigger } from '@/app/Components/EntityPreview';
import {
  findAssistantEntityMentions,
  isSafeAssistantHref,
  mentionFromInternalHref,
  type AssistantEntityMention,
} from '@/app/lib/assistant-v2/entityMentions';
import { useCoursePageLinkClick } from '@/app/Components/CoursePeek/CourseLink';

function MentionAnchor({ mention, children }: { mention: AssistantEntityMention; children: ReactNode }) {
  const openCoursePage = useCoursePageLinkClick();
  return (
    <EntityPreviewTrigger
      label={`${mention.title} preview`}
      preview={<EntityPreviewCard eyebrow={mention.eyebrow} title={mention.title} metadata={mention.metadata} description={mention.description} />}
    >
      {(props) => (
        <Link
          ref={props.ref as RefCallback<HTMLAnchorElement>}
          href={mention.href}
          onClick={(event) => openCoursePage(event, mention.href)}
          aria-label={mention.title}
          aria-describedby={props['aria-describedby']}
          onMouseEnter={props.onMouseEnter}
          onMouseLeave={props.onMouseLeave}
          onFocus={props.onFocus}
          onBlur={props.onBlur}
          className="rounded-control font-medium text-[var(--ss-link)] underline decoration-dotted underline-offset-2 hover:-translate-y-px focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--ss-focus)] motion-reduce:transform-none"
        >
          {children}
        </Link>
      )}
    </EntityPreviewTrigger>
  );
}

export function renderAssistantMentionText(text: string): ReactNode {
  const mentions = findAssistantEntityMentions(text);
  if (!mentions.length) return text;
  const parts: ReactNode[] = [];
  let cursor = 0;
  mentions.forEach((mention, index) => {
    if (mention.start > cursor) parts.push(text.slice(cursor, mention.start));
    parts.push(<MentionAnchor key={`${mention.kind}-${mention.start}-${index}`} mention={mention}>{mention.match}</MentionAnchor>);
    cursor = mention.end;
  });
  if (cursor < text.length) parts.push(text.slice(cursor));
  return parts;
}

export function withAssistantMentions(children: ReactNode): ReactNode {
  return Children.map(children, (child) => (typeof child === 'string' ? renderAssistantMentionText(child) : child));
}

export function AssistantSafeLink({ href, children }: { href?: string; children: ReactNode }) {
  const safeHref = isSafeAssistantHref(href);
  if (!safeHref) return <span>{children}</span>;
  const label = typeof children === 'string' ? children : '';
  const mention = safeHref.startsWith('/') ? mentionFromInternalHref(safeHref, label) : null;
  if (mention) {
    return <MentionAnchor mention={{ ...mention, href: safeHref, match: label || mention.match }}>{children}</MentionAnchor>;
  }
  if (safeHref.startsWith('/')) {
    return <Link href={safeHref} className="font-medium text-[var(--ss-link)] underline underline-offset-2">{children}</Link>;
  }
  return <a href={safeHref} target="_blank" rel="noopener noreferrer">{children}</a>;
}
